/**
 * Backdrop blur engine (UI-16): snapshots the backbuffer and runs a dual-kawase chain on
 * downscaled LDR scratch targets. Owned lazily by UIBatch — one instance per render context,
 * resources allocated on first acrylic node and resized with the viewport.
 *
 * Contract: `blur()` is called MID-UI-PASS with the backbuffer bound (UIBatch flushed first).
 * It ends/reopens passes freely (they reload content) and returns with the BACKBUFFER re-bound
 * and the viewport restored — only scissor state is the caller's job (flush re-applies it).
 */
import {
  NULL_HANDLE,
  getCompiledShader,
  type IRenderContext,
  type PipelineDescriptor,
  type PipelineHandle,
  type RenderTargetHandle,
  type ShaderHandle,
  type TextureHandle,
} from '@engine/render2d';

/** Downsample levels: /2 /4 /8 /16 of the viewport. 4 dual-kawase iterations ≈ 24px+ blur. */
const MAX_LEVELS = 4;

export class UIBackdrop {
  private readonly ctx: IRenderContext;

  private width = 0;
  private height = 0;
  private copyTarget: RenderTargetHandle = NULL_HANDLE;
  private levels: RenderTargetHandle[] = [];

  private blurShader: ShaderHandle = NULL_HANDLE;
  private upShader: ShaderHandle = NULL_HANDLE;
  private downPipeline: PipelineHandle = NULL_HANDLE;
  private upPipeline: PipelineHandle = NULL_HANDLE;

  private readonly packed = new Float32Array(4);
  private _warnedUnsupported = false;

  constructor(ctx: IRenderContext) {
    this.ctx = ctx;
  }

  /**
   * Blur what's currently on the backbuffer. Returns the blurred texture (≤ half res — sample
   * by screen UV, bilinear hides the scale) or NULL_HANDLE when the host can't copy the
   * swapchain (graceful degrade: the caller skips the backdrop draw).
   */
  blur(width: number, height: number, blurPx: number): TextureHandle {
    const { ctx } = this;
    if (!ctx.copyBackbufferToTexture) {
      this.warnUnsupported();
      return NULL_HANDLE;
    }
    this.ensureResources(width, height);

    const copyTex = ctx.getRenderTargetTexture(this.copyTarget);
    if (!ctx.copyBackbufferToTexture(copyTex, width, height)) {
      this.warnUnsupported();
      // the failed copy may have ended the pass — leave the context in the promised state
      ctx.setRenderTarget(null);
      ctx.setViewport(0, 0, width, height);
      return NULL_HANDLE;
    }

    // Iterations ~double the radius each level (dual-kawase); offset fine-tunes in between.
    const iterations = blurPx <= 4 ? 1 : blurPx <= 9 ? 2 : blurPx <= 18 ? 3 : MAX_LEVELS;
    const offset = Math.min(2, Math.max(0.75, blurPx / 2 ** (iterations + 1)));

    let srcTex = copyTex;
    for (let i = 0; i < iterations; i++) {
      this.fullscreen(this.downPipeline, this.levels[i]!, this.levelW(i), this.levelH(i), offset, srcTex);
      srcTex = ctx.getRenderTargetTexture(this.levels[i]!);
    }
    for (let i = iterations - 2; i >= 0; i--) {
      this.fullscreen(this.upPipeline, this.levels[i]!, this.levelW(i), this.levelH(i), offset, srcTex);
      srcTex = ctx.getRenderTargetTexture(this.levels[i]!);
    }

    ctx.setRenderTarget(null);
    ctx.setViewport(0, 0, width, height);
    return srcTex;
  }

  private fullscreen(
    pipeline: PipelineHandle,
    target: RenderTargetHandle,
    w: number,
    h: number,
    offset: number,
    source: TextureHandle,
  ): void {
    const { ctx } = this;
    ctx.setRenderTarget(target);
    ctx.setViewport(0, 0, w, h);
    ctx.disableScissor();
    ctx.setPipeline(pipeline);
    this.packed[0] = 1 / w;
    this.packed[1] = 1 / h;
    this.packed[2] = offset;
    // PP snapshot contract: uniforms → capture → THEN bind (the bind group latches the buffer)
    ctx.setUniform('packed', this.packed);
    ctx.captureUniforms?.();
    ctx.bindTexture(0, source);
    ctx.draw(3);
  }

  private levelW(i: number): number {
    return Math.max(1, this.width >> (i + 1));
  }
  private levelH(i: number): number {
    return Math.max(1, this.height >> (i + 1));
  }

  private ensureResources(width: number, height: number): void {
    const { ctx } = this;
    if (this.blurShader === NULL_HANDLE) {
      const down = getCompiledShader('ui/ui-blur-down');
      const up = getCompiledShader('ui/ui-blur-up');
      this.blurShader = ctx.createShader(down, down);
      const upShader = ctx.createShader(up, up);
      const desc: Omit<PipelineDescriptor, 'shader' | 'label'> = {
        vertexBuffers: [],
        colorTargets: [{ format: ctx.getSwapChainFormat() }],
        primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      };
      this.downPipeline = ctx.createPipeline({ ...desc, shader: this.blurShader, label: 'UIBackdrop-down' });
      this.upPipeline = ctx.createPipeline({ ...desc, shader: upShader, label: 'UIBackdrop-up' });
      this.upShader = upShader;
    }
    if (width === this.width && height === this.height) return;
    this.destroyTargets();
    this.width = width;
    this.height = height;
    this.copyTarget = ctx.createRenderTarget(width, height, false, false);
    for (let i = 0; i < MAX_LEVELS; i++) {
      this.levels.push(ctx.createRenderTarget(this.levelW(i), this.levelH(i), false, false));
    }
  }

  private warnUnsupported(): void {
    if (this._warnedUnsupported) return;
    this._warnedUnsupported = true;
    console.warn('[ui] backdrop blur unavailable on this host (no backbuffer copy) — panels render un-blurred');
  }

  private destroyTargets(): void {
    if (this.copyTarget !== NULL_HANDLE) this.ctx.destroyRenderTarget(this.copyTarget);
    this.copyTarget = NULL_HANDLE;
    for (const rt of this.levels) this.ctx.destroyRenderTarget(rt);
    this.levels = [];
  }

  destroy(): void {
    this.destroyTargets();
    if (this.downPipeline !== NULL_HANDLE) this.ctx.destroyPipeline(this.downPipeline);
    if (this.upPipeline !== NULL_HANDLE) this.ctx.destroyPipeline(this.upPipeline);
    if (this.blurShader !== NULL_HANDLE) this.ctx.destroyShader(this.blurShader);
    if (this.upShader !== NULL_HANDLE) this.ctx.destroyShader(this.upShader);
    this.downPipeline = this.upPipeline = this.blurShader = this.upShader = NULL_HANDLE;
    this.width = this.height = 0;
  }
}
